<template>
  <SmallPopup
    show
    cardClass="max-h-full w-full max-w-195 bg-popover-solid"
    contentClass="min-h-0 gap-3.5 p-4 sm:p-5"
    :showClose="false"
    @close="$emit('close')"
  >
    <div class="flex items-center gap-3">
      <div class="flex size-10 shrink-0 items-center justify-center rounded-control border border-accent/30 bg-accent-soft text-accent">
        <IconSparkles class="size-5" />
      </div>
      <div class="min-w-0 text-lg font-bold text-text">{{ $t('menu.ai_assistant') }}</div>
      <CloseButton class="ml-auto" @click="$emit('close')" />
    </div>

    <div class="-mx-4 border-t border-line sm:-mx-5"></div>

    <div ref="messagesEl" class="v-tracklify-scroll flex min-h-80 max-h-155 flex-col gap-3 overflow-y-auto">
      <div v-if="messages.length === 0" class="m-auto text-body text-text-muted">
        {{ $t('ui.ai_chat_empty') }}
      </div>
      <div
        v-for="(msg, i) in messages"
        :key="i"
        class="flex"
        :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
      >
        <div
          :class="[BUBBLE, msg.role === 'user' ? 'rounded-br-sm bg-accent text-on-accent' : 'rounded-bl-sm bg-input text-text']"
          v-html="renderMarkdown(msg.content)"
        ></div>
      </div>
      <div v-if="loading" class="flex justify-start">
        <div class="flex items-center gap-1.5 rounded-card rounded-bl-sm bg-input px-4 py-3.5">
          <span
            v-for="dot in 3"
            :key="dot"
            class="size-1.75 animate-bounce rounded-full bg-text-muted"
            :style="{ animationDelay: `${(dot - 1) * 0.2}s` }"
          ></span>
        </div>
      </div>
    </div>

    <div class="-mx-4 border-t border-line sm:-mx-5"></div>

    <EditorInput
      class="w-full [&>*]:max-h-135! [&_.quill-float-btn>*:not(:last-child)]:hidden"
      :reversedTag="true"
      :alwaysOpen="true"
      :isHideFormat="true"
      :mentionButton="false"
      :attachmentButton="false"
      :allowSubmit="!loading"
      maxHeight="440px"
      @submit="onEditorSubmit"
    />
  </SmallPopup>
</template>

<script setup>
import { ref, nextTick } from 'vue';
import { IconSparkles } from '@iconify-prerendered/vue-heroicons';
import { callAPI } from '@/store/socket';
import MarkdownIt from 'markdown-it';
import EditorInput from '@/components/elements/EditorInput.vue';
import { useRouter } from 'vue-router';
import CloseButton from '@/components/elements/CloseButton.vue';
import SmallPopup from '@/components/elements/SmallPopup.vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

// chat bubble + the markdown the assistant replies with
const BUBBLE = 'max-w-[80%] rounded-card px-3.5 py-2.5 text-body leading-normal wrap-anywhere '
  + '[&_p]:m-0 [&_p:not(:last-child)]:mb-1.5 [&_ol]:my-1 [&_ol]:pl-4.5 [&_ul]:my-1 [&_ul]:pl-4.5 '
  + '[&_code]:rounded-sm [&_code]:bg-col-bg [&_code]:px-1 [&_code]:text-label '
  + '[&_pre]:overflow-x-auto [&_pre]:rounded-control [&_pre]:bg-col-bg [&_pre]:p-2';

const mdParser = new MarkdownIt();

const emit = defineEmits(['close']);

const messages = ref([]);
const loading = ref(false);
const messagesEl = ref(null);

const router = useRouter();
const store = useStore();
const { t } = useI18n();

function renderMarkdown(text) {
  return mdParser.render(text || '');
}

async function scrollToBottom() {
  await nextTick();
  if (messagesEl.value) {
    messagesEl.value.scrollTop = messagesEl.value.scrollHeight;
  }
}

async function onEditorSubmit({ text }) {
  const projectName = router.currentRoute.value.params.slugNameProject;
  const trimmed = (text || '').trim();
  if (!trimmed || loading.value) return;

  messages.value.push({ role: 'user', content: trimmed });
  loading.value = true;
  await scrollToBottom();

  try {
    const apiMessages = messages.value.map(m => ({ role: m.role, content: m.content }));
    const response = await callAPI({
      method: 'aiChat',
      params: { messages: apiMessages, projectName },
      timeoutSeconds: 120,
      dontRetry: true,
    });
    if (response?.code === 'OPENAI_AUTH_ERROR') {
      store.commit('system/showAlert', {
        type: 'warning',
        message: t('ui.ai_chat_openai_auth_error'),
        timeout: 8,
        teleport: true,
      });
    } else {
      messages.value.push({ role: 'assistant', content: response.reply });
    }
  } catch (e) {
    messages.value.push({ role: 'assistant', content: t('ui.ai_chat_error', { error: e.message || t('ui.ai_chat_something_went_wrong') }) });
  } finally {
    loading.value = false;
    await scrollToBottom();
  }
}
</script>
